import { Button, Callout, Heading, Panel, Text, toast } from "@imagedesignai/neo";
import { useCallback, useEffect, useState } from "react";
import type { ProviderProfileId } from "../../src/provider-profiles.ts";
import type { GifmeApiClient, ProviderRegistryView } from "./api-client.ts";

export interface ModelRegistryPanelProps {
  api: GifmeApiClient;
}

export function ModelRegistryPanel({ api }: ModelRegistryPanelProps) {
  const [registry, setRegistry] = useState<ProviderRegistryView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activating, setActivating] = useState<ProviderProfileId | null>(null);

  const load = useCallback(async () => {
    const payload = await api.json<{ registry: ProviderRegistryView }>("/api/model-registry");
    setRegistry(payload.registry);
    setLoadError(null);
  }, [api]);

  useEffect(() => {
    let isMounted = true;
    void load().catch((error) => {
      if (isMounted) setLoadError(error instanceof Error ? error.message : String(error));
    });
    return () => {
      isMounted = false;
    };
  }, [load]);

  const activate = async (profileId: ProviderProfileId) => {
    if (!registry || profileId === registry.activeProfileId) return;
    setActivating(profileId);
    setLoadError(null);
    try {
      const payload = await api.json<{ registry: ProviderRegistryView }>(
        "/api/model-registry/active",
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ profileId, expectedRevision: registry.revision }),
        },
      );
      setRegistry(payload.registry);
      toast.positive("Provider profile activated");
    } catch (error) {
      try {
        await load();
      } catch {
        // Preserve the activation error when the refresh is also unavailable.
      }
      setLoadError(error instanceof Error ? error.message : String(error));
    } finally {
      setActivating(null);
    }
  };

  if (!registry) {
    return loadError ? (
      <Callout intent="danger" label="Could not load model registry">
        {loadError}
      </Callout>
    ) : (
      <Panel title="Provider model registry" icon="settings" className="model-registry-panel">
        <Text kind="body" tone="muted">
          Loading profiles…
        </Text>
      </Panel>
    );
  }

  return (
    <Panel title="Provider model registry" icon="settings" className="model-registry-panel">
      <div className="model-registry-content">
        {loadError ? (
          <Callout intent="danger" label="Registry changed while activating">
            {loadError}. The latest registry state is shown.
          </Callout>
        ) : null}
        <div className="model-profile-grid">
          {registry.profiles.map((profile) => {
            const isActive = profile.id === registry.activeProfileId;
            return (
              <section className="model-profile-card" key={profile.id} aria-current={isActive}>
                <div className="model-profile-heading">
                  <div>
                    <Heading level={2} scale="sm">
                      {profile.label}
                    </Heading>
                    <Text kind="caption" tone="muted">
                      {profile.id}
                    </Text>
                  </div>
                  <span className={isActive ? "profile-status is-active" : "profile-status"}>
                    {isActive ? "Active" : "Available"}
                  </span>
                </div>
                <dl className="model-profile-facts">
                  <div>
                    <dt>Endpoint</dt>
                    <dd className="mono">
                      {profile.endpoint.method} {profile.endpoint.path}
                    </dd>
                  </div>
                </dl>
                <Button
                  type="button"
                  intent={isActive ? "secondary" : "primary"}
                  loading={activating === profile.id}
                  disabled={isActive || activating !== null}
                  onClick={() => void activate(profile.id)}
                >
                  {isActive ? "Currently active" : "Activate profile"}
                </Button>
              </section>
            );
          })}
        </div>
      </div>
    </Panel>
  );
}
